/**
 * products.service.ts
 *
 * Typed API wrappers for the Products admin domain.
 * Uses the `/products` endpoint (ProductsController) which returns full
 * relationship data including facilities, product types, and store status.
 */
import { apiClient } from './apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProductCategory {
  quoteItemCostsCategoryID: number;
  title: string;
  /** Soft-delete flag. Absent on older API responses — treat missing as active. */
  active?: number | boolean | string;
}

export interface ProductFacilityRelation {
  facilityID: number;
  facilityName: string | null;
  pivot: {
    accountID: number | null;
    status: number | null;
    createUserID: number | null;
    stopDate: string | null;
    createDate: string | null;
  };
}

export interface ProductImage {
  productImageID: number;
  productUrl: string | null;
  /** 1 / '1' marks the image to show first. Legacy column, so both shapes. */
  defaultUrl: number | string | null;
  description: string | null;
}

export interface ProductTypeRelation {
  productTypeID: number;
  status: number | null;
  publicStatus: string | null;
  productTypeName: string | null;
  /** ProductsController returns these under productTypeRelation, not at the
   *  top of `relationships` — which is where ProductsAdmin was looking. */
  images: ProductImage[];
}

export interface ProductAddon {
  productAddonID: number;
  quoteItemCostID: number;
  productTypeID: number | null;
  accountID: number;
  availabilityStatus: string | null;
  /**
   * Legacy column, and it arrives as either shape depending on the
   * endpoint — ProductsController passes it through while
   * GroupedProductsController casts it with (int). Declared as both so
   * ProductsAdmin's `ps === '1' || ps === 1` is not comparing against a
   * type it had been told was impossible.
   */
  publicStatus: string | number | null;
  curMemberRate: string | null;
  /**
   * Which `shipping_fees` row this product carries; 0 for none. The fee is
   * charged once per payment regardless of quantity, and is never taxed or
   * discounted.
   */
  shippingFeeID?: number;
  availableToAllMemberships?: number | boolean | null;
  requestableByNonMembers?: boolean;
  /** 0 = for sale, 1 = requestable. */
  saleType?: number;
  sendLead?: number | boolean | null;
  allowPriceOverride?: number | boolean | null;
  status: number | null;
  updateDate: string | null;
  createDate: string | null;
}

export interface ProductRelationships {
  category: ProductCategory | null;
  facility: { facilityID: number; facilityName: string | null } | null;
  productTypeRelation: ProductTypeRelation | null;
  productFacilities: ProductFacilityRelation[];
  productAddon: ProductAddon | null;
  /** Present on GET /products/{id}; the list leaves it out. */
  membershipTemplates?: { membershipTemplateID: number; title: string | null }[];
}

export interface ProductFull {
  quoteItemCostID: number;
  accountID: number;
  strItem: string;
  strDescription: string | null;
  curRate: string | number;
  active: number | boolean;
  taxable: number | boolean;
  isInventory: number | boolean;
  quantityRemaining: number | null;
  facilityID: number | null;
  quoteItemCostsCategoryID: number | null;
  lngOrder: number | null;
  /** 0 = Standard, 1 = Gift Credit. */
  productType?: number | null;
  /** Detail (GET /products/{id}) only — the list omits these three. */
  strUnit?: string | null;
  parLevel?: number | null;
  verifyInterval?: number | null;
  relationships: ProductRelationships;
  [key: string]: unknown;
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

/**
 * Load all active products in a single request (no pagination limit).
 * The API wraps a Laravel paginator in successResponse(), producing:
 *   { data: { data: [...products], ... }, meta: {} }
 * Setting limit=0 tells the API to return everything in one page.
 *
 * Options (Admin > Products' "Show deleted"):
 *   - active: 0 returns ONLY soft-deleted products (the API's default is
 *     active only; there is no "both" value);
 *   - includeDeletedCategories also returns products filed under a deleted
 *     category (hidden by default).
 */
export interface ListProductsOptions {
  active?: 0;
  includeDeletedCategories?: boolean;
}

export async function listAllProducts(options: ListProductsOptions = {}): Promise<ProductFull[]> {
  const params: Record<string, number> = { limit: 9999 };
  if (options.active === 0) params.active = 0;
  if (options.includeDeletedCategories) params.include_deleted_categories = 1;
  const res = await apiClient.get<{
    data:
      | ProductFull[]
      | {
          data: ProductFull[];
        };
  }>('/products', { params });

  const outer = res.data.data;
  if (Array.isArray(outer)) return outer;
  return Array.isArray(outer.data) ? outer.data : [];
}

/** Extract the product from a response (may be nested). */
function unwrapProduct(resData: Record<string, unknown>): ProductFull {
  const d = resData.data;
  if (d && typeof d === 'object' && 'quoteItemCostID' in d) return d as ProductFull;
  return resData as unknown as ProductFull;
}

/** Full update of a product — sends all editable fields. */
export async function updateProduct(
  productId: number,
  payload: Record<string, unknown>,
): Promise<ProductFull> {
  const res = await apiClient.put<Record<string, unknown>>(
    `/products/${productId}`,
    payload,
  );
  return unwrapProduct(res.data);
}

/** One change to a product's "Available in Store" flag. */
export interface ProductStoreEvent {
  action: 'added' | 'removed';
  /** v5 = a staff member here; corp_dashboard = the corporate dashboard; api_client = another system. */
  source: 'v5' | 'corp_dashboard' | 'api_client';
  userName: string | null;
  /** ISO-8601 with offset. */
  at: string;
}

/** Who added this product to the store or removed it, newest first. */
export async function getStoreHistory(productId: number): Promise<ProductStoreEvent[]> {
  const res = await apiClient.get<{ data?: ProductStoreEvent[] }>(`/products/${productId}/store-history`);
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

/** Create a new product. */
export async function createProduct(
  payload: Record<string, unknown>,
): Promise<ProductFull> {
  const res = await apiClient.post<Record<string, unknown>>(
    '/products',
    payload,
  );
  return unwrapProduct(res.data);
}

/** Soft-delete a product (sets active=0). */
export async function deleteProduct(productId: number): Promise<void> {
  await apiClient.delete(`/products/${productId}`);
}

/** Restore a soft-deleted product (sets active=1). */
export async function restoreProduct(productId: number): Promise<void> {
  await apiClient.post(`/products/${productId}/restore`);
}

/**
 * One product with every field — the list omits strUnit, parLevel,
 * verifyInterval and membership templates, which the edit form needs.
 */
export async function getProduct(productId: number): Promise<ProductFull> {
  const res = await apiClient.get<Record<string, unknown>>(`/products/${productId}`);
  return unwrapProduct(res.data);
}

/**
 * Save one category's products in their shown order. `productIds` is the
 * category's FULL list after a drop: each gets lngOrder = its 1-based
 * position, and any that came from another category are moved into this one.
 */
export async function reorderProducts(categoryId: number, productIds: number[]): Promise<void> {
  await apiClient.post('/products/reorder', { categoryId, productIds });
}
